import { useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import styled from "styled-components/macro";
import { VisibleIcon } from "../Icons/Icons";
import { spacingTokens } from "../../designTokens";
import { screenSize } from "../../theme";

const Header = styled.div`
  font-family: ${({ theme }) => theme.resolve("font_family_primary")};
  font-weight: ${({ theme }) => theme.resolve("font_weight_medium")};
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};
  color: ${({ theme }) => theme.resolve("color_text_tertiary")};
  line-height: ${({ theme }) => theme.resolve("line_height_sm")};
`;

const LargeContent = styled.div`
  font-weight: ${({ theme }) => theme.fontWeights.large};
  font-size: ${({ theme }) => theme.fontSizes.large};
  line-height: 1.45;
`;

const IconContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
`;

const InfoBlockExtraSmallContainer = styled.div`
  max-width: 100%;
`;

interface IInfoBlockLargeProps {
  header: string;
  content: JSX.Element | string;
  icon?: any;
  callback?: any;
}

export const InfoBlockLarge = ({
  header,
  content,
  icon,
  callback,
}: IInfoBlockLargeProps) => {
  const [hover, setHover] = useState(false);
  if (icon !== undefined && callback !== undefined) {
    return (
      <div>
        <Header>{header}</Header>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <LargeContent>{content}</LargeContent>
          <span
            onClick={callback}
            style={{
              cursor: hover ? "pointer" : "none",
              marginLeft: "5px",
            }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
          >
            {icon}
          </span>
        </div>
      </div>
    );
  } else {
    return (
      <div>
        <Header>{header}</Header>
        <LargeContent>{content}</LargeContent>
      </div>
    );
  }
};

const MediumContent = styled.div`
  display: inline-block;
  font-family: ${({ theme }) => theme["fontFamily"]};
  font-weight: ${({ theme }) => theme.fontWeights["medium"]};
  font-size: ${({ theme }) => theme.fontSizes["medium"]};
  line-height: 1.45;
`;

interface IInfoBlockMediumProps {
  header: string;
  content: JSX.Element | string;
  icon?: any;
}

export const InfoBlockMedium = ({
  header,
  content,
  icon,
}: IInfoBlockMediumProps) => (
  <>
    <Header>{header}</Header>
    <IconContainer>
      <span style={{ marginRight: spacingTokens.spacing_xs }}>{icon}</span>
      <MediumContent>{content}</MediumContent>
    </IconContainer>
  </>
);

const SmallContent = styled.div`
  margin: 0;
  font-family: ${({ theme }) => theme.resolve("font_family_primary")};
  font-weight: ${({ theme }) => theme.resolve("font_weight_regular")};
  color: ${({ theme }) => theme.resolve("color_text_primary")};
  font-size: ${({ theme }) => theme.resolve("font_size_md")};
  line-height: ${({ theme }) => theme.resolve("line_height_md")};
  max-width: 100%;
`;

const InfoSmallBlockWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${({ theme }) => theme.resolve("spacing_xxs")};
`;

interface IInfoBlockSmallProps {
  header: string;
  content?: JSX.Element | string;
  icon?: any;
  testid?: string;
}

export const InfoBlockSmall = ({
  header,
  content,
  icon,
  testid,
}: IInfoBlockSmallProps) => {
  return (
    <InfoSmallBlockWrapper data-testid={testid}>
      <Header>{header}</Header>
      {icon ? (
        <IconContainer>
          <span style={{ marginRight: spacingTokens.spacing_xs }}>{icon}</span>
          <SmallContent>{content}</SmallContent>
        </IconContainer>
      ) : (
        <SmallContent>{content}</SmallContent>
      )}
    </InfoSmallBlockWrapper>
  );
};

const ContentExtraSmall = styled(SmallContent)`
  font-size: ${({ theme }) => theme.fontSizes.small};
`;

const HeaderExtraSmall = styled(Header)`
  font-size: ${({ theme }) => theme.fontSizes.xs};
`;

const IconWrapper = styled.span`
  margin: ${({ theme }) => theme.resolve("spacing_xs")}
    ${({ theme }) => theme.resolve("spacing_xs")} 0 0;
`;

export const InfoBlockExtraSmall = ({
  header,
  content,
  icon,
  testid,
}: IInfoBlockSmallProps) => {
  return (
    <InfoBlockExtraSmallContainer data-testid={testid}>
      <HeaderExtraSmall>{header}</HeaderExtraSmall>
      {icon ? (
        <IconContainer>
          <IconWrapper>{icon}</IconWrapper>
          <ContentExtraSmall>{content}</ContentExtraSmall>
        </IconContainer>
      ) : (
        <ContentExtraSmall>{content}</ContentExtraSmall>
      )}
    </InfoBlockExtraSmallContainer>
  );
};

const MobileBlock = styled.div`
  display: inline-block;
  font-family: ${({ theme }) => theme["fontFamily"]};
  font-weight: ${({ theme }) => theme.fontWeights["extra-small-body-plus"]};
  font-size: ${({ theme }) => theme.fontSizes["extra-small-body-plus"]};
`;

interface IMobileInfoBlockProps {
  ask: any;
  left: any;
}

export const InfoBlockMobile = ({ ask, left }: IMobileInfoBlockProps) => (
  <MobileBlock>
    <span style={{ marginRight: spacingTokens.spacing_xs }}>
      <VisibleIcon width={13} height={9} />
    </span>
    {ask} / {left}
  </MobileBlock>
);

const GreyLine = styled.div`
  width: 1px;
  height: 100%;
  border: solid ${({ theme }) => theme.resolve("border_2xl")}
    ${({ theme }) => theme.resolve("color_border_lighter")};
  margin-right: 15px;
`;
const GreyLineContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
`;
const GreyLineContent = styled.span`
  font-family: "Inter";
  font-size: 11px;
  line-height: 1.45;
  color: #6f777f;
`;
interface IGreyLineBlock {
  content: string;
}

export const GreyLineBlock = ({ content }: IGreyLineBlock) => (
  <GreyLineContainer>
    <GreyLine />
    <GreyLineContent>{content}</GreyLineContent>
  </GreyLineContainer>
);

const InfoBlockRowGrid = styled.div<{ $columns: number }>`
  margin-bottom: ${({ theme }) => theme.resolve("spacing_xxs")};
  display: grid;
  /* +1 reserves a trailing empty column so the last item doesn't sit flush
     against the card's right edge, per design. */
  grid-template-columns: repeat(${({ $columns }) => $columns + 1}, 1fr);
  gap: ${({ theme }) => theme.resolve("spacing_3xl")};
  padding-top: ${({ theme }) => theme.resolve("spacing_3xl")};
  padding-bottom: ${({ theme }) => theme.resolve("spacing_3xl")};

  @media ${screenSize.medium} {
    grid-template-columns: 1fr;
  }
`;

interface IInfoBlockRowProps {
  // Number of items being rendered in the row (2-4 typically). The row
  // reserves one additional trailing column beyond this count.
  columns: number;
  children: ReactNode;
  style?: CSSProperties;
}

// Used to display info blocks in a row. Typically used with InfoBlockSmall.
export const InfoBlockRow = ({
  columns,
  children,
  style,
}: IInfoBlockRowProps) => (
  <InfoBlockRowGrid $columns={columns} style={style}>
    {children}
  </InfoBlockRowGrid>
);

// Used to add spacing between info blocks arranged in a column. Typically used
// with InfoBlockExtraSmall.
export const InfoBlockSpacer = styled.div`
  margin-top: 14px;
  margin-bottom: 10px;
`;

export const InfoBlockColumn = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${({ theme }) => theme.resolve("spacing_xxs")};
  padding-top: 5px;
  padding-bottom: 27px;
  div {
    margin-bottom: 5px;
  }
`;
